import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { useAuth } from "@shared/hooks/useAuth";
import { Button, Typography } from "@mui/material";
import { Form } from "@shared/components/Form";
import { PasswordTextField } from "@shared/components/PasswordTextField";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AuthContainer,
  ErrorMessage,
  ValidationSummaryContainer,
  ValidationSummaryItem,
} from "./Auth.styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AuthChallengeNewPasswordRequiredFormSchema,
  AUTH_CHALLENGE_NEW_PASSWORD_REQUIRED_SCHEMA_DEFAULT_VALUES,
  authChallengeNewPasswordRequiredFormSchema,
  passwordRules,
} from "./auth-form-helpers";
import { useMemo } from "react";
import { ValidationError } from "yup";
import { useChallengeNewPassword } from "@queries/auth/useChallengeNewPassword";
import { useDirectOAuth2Authorize } from "@queries/auth/useDirectOAuth2Authorize";
import { SubmitButton } from "@avenue-8/sidekick-ui";

type NewPasswordRequiredProps = {
  email: string;
  session: string;
  redirect?: string;
  oauthData: {
    clientId: string;
    scope: string;
    redirectUri: string;
    state?: string | null;
    responseType: string;
  } | null;
};

export const NewPasswordRequired = (props: NewPasswordRequiredProps) => {
  const { email, session, redirect = false, oauthData } = props;
  const { src: logoSrc, alt: logoAlt } = useAppLogoAsset();
  const { error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    trigger,
  } = useForm<AuthChallengeNewPasswordRequiredFormSchema>({
    defaultValues: {
      ...AUTH_CHALLENGE_NEW_PASSWORD_REQUIRED_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(authChallengeNewPasswordRequiredFormSchema),
    reValidateMode: "onChange",
  });
  const loginPath = location.search ? `/login${location.search}` : "/login";

  const directOAuth2AuthorizeMutation = useDirectOAuth2Authorize({
    onSuccess: (data) => {
      window.location.replace(data.redirectUrl);
    },
  });

  const challengeNewPassword = useChallengeNewPassword({
    onSuccess: () => {
      reset();
      if (oauthData) {
        directOAuth2AuthorizeMutation.mutate({
          ...oauthData,
        });
        return;
      }

      if (redirect) {
        window.location.replace(redirect);
        return;
      }
      navigate("/");
    },
    onError: (error) => {
      if (error.status === 401) {
        navigate("/login", { replace: true, state: null });
      }
    },
  });

  const handleConfirmResetPassword = (
    data: AuthChallengeNewPasswordRequiredFormSchema,
  ) => {
    challengeNewPassword.mutate({
      newPassword: data.newPassword,
      email,
      session,
    });
  };

  const [newPassword, newPassword2] = watch(["newPassword", "newPassword2"]);
  const isPasswordDirty = !!newPassword.trim() || !!newPassword2.trim();

  const newPasswordErrors = useMemo((): Array<string> => {
    try {
      if (newPassword2?.trim()) {
        trigger("newPassword2");
      }

      authChallengeNewPasswordRequiredFormSchema.validateSync(
        { newPassword, newPassword2: newPassword },
        { abortEarly: false },
      );

      return [];
    } catch (error) {
      if (error instanceof ValidationError) {
        return error.errors;
      }
      return ["Validation error"];
    }
  }, [newPassword, newPassword2, trigger]);

  if (challengeNewPassword.isSuccess) {
    return (
      <AuthContainer>
        <header>
          <img src={logoSrc} alt={logoAlt} />
        </header>
        <Typography mt={2} mb={2} textAlign={"center"}>
          Your password has been reset successfully. You can now sign in with
          your new password.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            navigate(loginPath, { state: { email }, replace: true })
          }
        >
          Sign in
        </Button>
      </AuthContainer>
    );
  }

  const newPasswordError = Object.values(passwordRules).includes(
    errors.newPassword?.message ?? "",
  )
    ? undefined
    : errors.newPassword;

  return (
    <AuthContainer>
      <header>
        <img src={logoSrc} alt={logoAlt} />
      </header>
      <div>
        <Typography variant="h6" mt={1}>
          Change Password
        </Typography>
        <Typography mt={1} mb={2}>
          Before you can continue, please enter a new password below. Once your
          new password is set, you will be able to proceed with using our
          services.
        </Typography>
      </div>

      <Form
        onSubmit={handleSubmit(handleConfirmResetPassword)}
        aria-disabled={challengeNewPassword.isPending}
      >
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
        <input
          type="text"
          id="email"
          name="email"
          aria-hidden="true"
          hidden
          autoComplete="email"
          defaultValue={email}
        />
        <PasswordTextField
          name={"newPassword"}
          label={"New Password"}
          autoComplete="new-password"
          inputProps={{ ...register("newPassword") }}
          fieldError={newPasswordError}
          autoFocus
        />
        <PasswordTextField
          name={"newPassword2"}
          label={"Confirm Password"}
          autoComplete="new-password"
          inputProps={{ ...register("newPassword2") }}
          fieldError={errors.newPassword2}
        />

        <ValidationSummaryContainer aria-hidden={!isPasswordDirty}>
          <Typography variant="body2" mb={0.5}>
            Password must:
          </Typography>
          {Object.entries(passwordRules).map(([key, value]) => {
            const isValid = !newPasswordErrors.includes(value);
            return (
              <ValidationSummaryItem
                variant="body2"
                color={isValid ? "success.main" : "primary"}
                key={key}
              >
                {isValid ? (
                  <CheckIcon sx={{ color: "success.main" }} fontSize="small" />
                ) : (
                  <CloseIcon sx={{ color: "primary" }} fontSize="small" />
                )}{" "}
                {value}
              </ValidationSummaryItem>
            );
          })}
        </ValidationSummaryContainer>

        <SubmitButton
          isLoading={challengeNewPassword.isPending}
          sx={{ marginTop: isPasswordDirty ? 0 : -1 }}
        >
          Save
        </SubmitButton>
        <Typography textAlign={"center"} mt={1}>
          <Link to={loginPath}>Back to sign in</Link>
        </Typography>
      </Form>
    </AuthContainer>
  );
};
